import { Trans, useLingui } from "@lingui/react/macro";
import { ChevronDown } from "lucide-react";
import { Fragment, useMemo, useState } from "react";

import type { TimeSeriesPoint } from "../../../api";

import { buttonVariants } from "../../../components/ui-kit/base/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../../components/ui-kit/base/dropdown-menu";
import {
  DataZoomRange,
  EChartsChart,
  EChartsOption,
} from "../../../components/ui-kit/echarts-chart";
import { CHART_COLORS } from "./heartcore-chart-options";
import {
  DAY_MS,
  formatChartDate,
  formatChartDateTime,
} from "./heartcore-format";

export function ChartSection(props: {
  title: string;
  option: EChartsOption;
  traces: StatTrace[];
  /** Shown once next to the title — never repeated on the numbers. */
  unit?: string;
  /** Decimals for every number in the summary. Defaults to 0. */
  decimals?: number;
  /** Chart height in px. Defaults to 320. */
  chartHeight?: number;
  /** Rules drawn across the full chart width (incl. the axis-name gutter) at
   *  these px offsets from its top — the symptoms chart's sub-grid dividers. */
  subDividers?: number[];
  /** Shared (lifted) axis-pointer timestamp in ms. */
  axisTimeMs: number | null;
  onAxisTimeChange: (v: number | null) => void;
  /** Shared (lifted) zoom range as ECharts dataZoom percentages. */
  dataZoomRange: DataZoomRange | null;
  /** Narrow container: put the summary panel above the plot, not beside it. */
  stacked?: boolean;
}) {
  const { t } = useLingui();
  const { axisTimeMs } = props;
  const decimals = props.decimals ?? 0;

  // Stores only explicit user toggles; an absent key means "use the trace's
  // default" — this keeps default-off traces correct when traces arrive after
  // mount, which seeding the state wouldn't.
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const isActive = (t: StatTrace) =>
    selected[traceKey(t)] ?? traceDefaultActive(t);
  const toggle = (t: StatTrace) => {
    const key = traceKey(t);
    setSelected((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? traceDefaultActive(t)),
    }));
  };
  const selectedSeries = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const t of props.traces) {
      const active = selected[traceKey(t)] ?? traceDefaultActive(t);
      // OR, not last-write-wins: a series named by several rows (e.g. shared
      // threshold lines) stays visible while any of those rows is active.
      for (const name of traceSeries(t))
        map[name] = (map[name] ?? false) || active;
    }
    return map;
  }, [props.traces, selected]);

  // Everything except the CURRENT row, which lives in its own memo below so
  // axis-pointer moves don't recompute the window averages.
  const stats = useMemo(
    () =>
      props.traces.map((t) => {
        const nonNull = t.points.filter((p) => p.value !== null);
        const lv = nonNull.at(-1) ?? null;
        const prevLv = nonNull.at(-2) ?? null;
        const lastMs = lv ? new Date(lv.timestamp).getTime() : null;
        // Trailing N-day average anchored at the last reading, and the average
        // of the equally-sized window immediately before it (for the % badge).
        const avgLast = (days: number) =>
          lastMs === null
            ? null
            : avgInWindow(nonNull, lastMs - days * DAY_MS, lastMs + 1);
        const avgPrior = (days: number) =>
          lastMs === null
            ? null
            : avgInWindow(
                nonNull,
                lastMs - 2 * days * DAY_MS,
                lastMs - days * DAY_MS,
              );
        const avg7d = avgLast(7);
        const avg30d = avgLast(30);
        return {
          lvValue: lv?.value ?? null,
          lvTime: lv?.timestamp ?? null,
          lvPct: pctChange(lv?.value ?? null, prevLv?.value ?? null),
          avg7d,
          avg7dPct: pctChange(avg7d, avgPrior(7)),
          avg30d,
          avg30dPct: pctChange(avg30d, avgPrior(30)),
        };
      }),
    [props.traces],
  );

  // CURRENT row: per-trace value nearest the shared axis cursor.
  const currents = useMemo(
    () =>
      axisTimeMs === null
        ? null
        : props.traces.map((t) => valueAtTime(t.points, axisTimeMs)),
    [props.traces, axisTimeMs],
  );

  const currentTimeLabel = (() => {
    const ts = currents?.find((c) => c.timestamp)?.timestamp;
    if (!ts) return null;
    return formatChartDateTime(ts);
  })();

  const lvTimeLabel = (() => {
    const ts = stats.find((s) => s.lvTime)?.lvTime;
    if (!ts) return null;
    return formatChartDate(ts);
  })();

  // One column per *selected* trace, all on one row. Columns can't shrink past
  // VALUE_COL_MIN, so once a chart carries more selected traces than the panel
  // fits (the HR source charts) the row overflows and scrolls sideways.
  const columns: StatColumn[] = props.traces
    .map((trace, i) => ({
      // Index, not the trace key: a report with duplicate facets yields
      // repeated trace names, and React keys must stay unique.
      key: `${i}-${traceKey(trace)}`,
      trace,
      stat: stats[i],
      current: currents?.[i]?.value ?? null,
    }))
    .filter((c) => isActive(c.trace));

  // Row order matches the old column order: newest-first, then the averages.
  const rows: StatRow[] = [
    {
      key: "current",
      label: t`CURRENT`,
      sublabel: currentTimeLabel ?? "—",
      value: (c) => c.current,
      pct: () => null,
    },
    {
      key: "last",
      label: t`LAST VALUE`,
      sublabel: lvTimeLabel ?? "—",
      value: (c) => c.stat.lvValue,
      pct: (c) => c.stat.lvPct,
    },
    {
      key: "avg7",
      label: t`7D AVG`,
      value: (c) => c.stat.avg7d,
      pct: (c) => c.stat.avg7dPct,
    },
    {
      key: "avg30",
      label: t`30D AVG`,
      value: (c) => c.stat.avg30d,
      pct: (c) => c.stat.avg30dPct,
    },
  ];

  // Only the trace headers carry colour; the numbers stay in the body ink so a
  // stack of charts doesn't read as a colour chart of its own.
  const renderValue = (value: number | null) =>
    value === null ? (
      <span className="text-muted-foreground">—</span>
    ) : (
      // A step up from the grid's text-xs: the numbers are what the panel is
      // for, and there is spare height under the legend to spend on them. The
      // % badge beside it deliberately stays small.
      <span className="text-foreground text-sm font-semibold tabular-nums">
        {value.toFixed(decimals)}
      </span>
    );

  return (
    <div
      // border-border: the same 1px rule the cards in the profile header use,
      // so a page of stacked charts reads as grouped sections instead of a
      // stack of hard-edged boxes.
      className={`border-border relative mb-2 flex border-b pb-2 ${
        // Panel first when stacked — it carries the chart's title.
        props.stacked ? "flex-col-reverse" : "items-start"
      }`}
      style={{ gap: SUMMARY_PANEL_GAP }}
    >
      <div className="relative min-w-0 flex-1">
        <EChartsChart
          option={props.option}
          overflowVisible
          axisPointerValue={axisTimeMs}
          onAxisPointerUpdate={props.onAxisTimeChange}
          dataZoomRange={props.dataZoomRange}
          selectedSeries={selectedSeries}
          rulerColor={CHART_COLORS.rulerInk}
          className="[&_svg]:overflow-visible"
          style={{ height: props.chartHeight ?? 320 }}
        />
        {/* Rules between stacked sub-charts. Drawn here rather than as each
            sub-grid's axis line, which can only ever span the plot area and so
            stopped short of the axis names it also needs to separate. Offsets
            come from the same geometry the grids are laid out with. */}
        {props.subDividers?.map((top) => (
          <div
            key={top}
            className="border-border pointer-events-none absolute inset-x-0 border-t"
            style={{ top }}
          />
        ))}
      </div>
      <div
        className={props.stacked ? "w-full" : "shrink-0"}
        style={{ width: props.stacked ? undefined : SUMMARY_PANEL_WIDTH }}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <h4 className="flex flex-wrap items-baseline gap-x-1.5 text-sm font-medium">
            {props.title}
            {props.unit && (
              <span className="text-muted-foreground text-xs font-normal">
                {props.unit}
              </span>
            )}
          </h4>
          {props.traces.length > 1 && (
            <TraceFilter
              traces={props.traces}
              isActive={isActive}
              onToggle={toggle}
              activeCount={columns.length}
            />
          )}
        </div>
        <div className="overflow-x-auto">
          <div
            // No column gap: the columns pad themselves instead, so the sticky
            // label cell's background sits flush against them and scrolled
            // numbers can't show through a gap beside it.
            className="grid items-start gap-y-2.5 text-xs"
            style={{
              // Stacked the panel spans the page, so columns size to their
              // numbers instead of stretching across it.
              gridTemplateColumns: `${LABEL_COL_WIDTH}px repeat(${columns.length}, minmax(${VALUE_COL_MIN}px, ${
                props.stacked ? "max-content" : "1fr"
              }))`,
            }}
          >
            {/* Header row: one toggle per trace, in the trace's colour. The
                spacer above the row labels is sticky for the same reason they
                are — see StickyLabelCell. */}
            <StickyLabelCell />
            {columns.map((c) => (
              <TraceHeader
                key={c.key}
                color={c.trace.color}
                label={c.trace.label}
                shape={c.trace.shape ?? "circle"}
              />
            ))}

            {rows.map((r) => (
              <Fragment key={r.key}>
                <StatLabel label={r.label} sublabel={r.sublabel} />
                {columns.map((c) => (
                  <ValueCell key={c.key}>
                    {renderValue(r.value(c))}
                    <PctBadge pct={r.pct(c)} />
                  </ValueCell>
                ))}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Fixed width of the summary panel to the right of every chart. Exported so
 *  the events + zoom bars reserve the same gutter — every chart must end at
 *  the same x for the shared time axes and the synced ruler to line up. */
export const SUMMARY_PANEL_WIDTH = 300;
export const SUMMARY_PANEL_GAP = 16;
export const SUMMARY_GUTTER = SUMMARY_PANEL_WIDTH + SUMMARY_PANEL_GAP;

/** Narrowest a plot may get beside its summary panel; below this the caller
 *  stacks the panel on top (`stacked`) instead of squeezing further. */
export const MIN_CHART_WIDTH = 460;

// Narrowest a trace column gets (including its 8px left padding) before the
// summary starts scrolling sideways instead. Three columns still fit the panel
// unscrolled; a fourth is what tips it over.
const VALUE_COL_MIN = 72;
// Fixed, not auto: an auto track gets squeezed to min-content as soon as the
// row overflows (wrapping "LAST VALUE"), and a fixed one also lines the value
// columns up at the same x on every chart.
const LABEL_COL_WIDTH = 78;

export type TraceShape = "circle" | "square" | "triangle" | "arrow";

export type StatTrace = {
  label: string;
  color: string;
  points: TimeSeriesPoint[];
  /** Locale-stable toggle identity — defaults to `seriesName`. Pass this
   *  whenever the series name is display text (a translated condition), or a
   *  locale switch resets the user's line selection. */
  key?: string;
  /** ECharts series name — defaults to `label`. */
  seriesName?: string;
  /** All series this row toggles together (defaults to just `seriesName`),
   *  e.g. a line plus its separate markers series. */
  seriesNames?: string[];
  /** Glyph shape for the toggle icon — defaults to circle. */
  shape?: TraceShape;
  /** Initial visibility; `false` starts the line hidden. Defaults to true. */
  visible?: boolean;
};

type StatColumn = {
  key: string;
  trace: StatTrace;
  stat: {
    lvValue: number | null;
    lvPct: number | null;
    avg7d: number | null;
    avg7dPct: number | null;
    avg30d: number | null;
    avg30dPct: number | null;
  };
  current: number | null;
};

type StatRow = {
  key: string;
  label: string;
  /** Timestamp the row's values come from, when they share one. */
  sublabel?: string;
  value: (c: StatColumn) => number | null;
  pct: (c: StatColumn) => number | null;
};

// The toggle's state key and default visibility for a stat-column trace.
function traceKey(t: StatTrace): string {
  return t.key ?? t.seriesName ?? t.label;
}

// The ECharts series this row toggles — the names in the option, which may be
// display text, unlike traceKey.
function traceSeries(t: StatTrace): string[] {
  return t.seriesNames ?? [t.seriesName ?? t.label];
}

function traceDefaultActive(t: StatTrace): boolean {
  return t.visible !== false;
}

function avgInWindow(
  points: TimeSeriesPoint[],
  startMs: number,
  endMs: number,
): number | null {
  let sum = 0;
  let count = 0;
  for (const p of points) {
    if (p.value === null) continue;
    const t = new Date(p.timestamp).getTime();
    if (t < startMs || t >= endMs) continue;
    sum += p.value;
    count++;
  }
  return count > 0 ? sum / count : null;
}

function valueAtTime(
  points: TimeSeriesPoint[],
  targetMs: number,
): { value: number | null; timestamp: string | null } {
  let best: TimeSeriesPoint | null = null;
  let bestDiff = Infinity;
  for (const p of points) {
    if (p.value === null) continue;
    const diff = Math.abs(new Date(p.timestamp).getTime() - targetMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = p;
    }
  }
  return { value: best?.value ?? null, timestamp: best?.timestamp ?? null };
}

function pctChange(
  current: number | null,
  prior: number | null,
): number | null {
  if (current === null || prior === null) return null;
  if (prior === 0) return null;
  return ((current - prior) / Math.abs(prior)) * 100;
}

// The row-label column stays pinned while the trace columns scroll under it —
// a bare number is meaningless without CURRENT / LAST VALUE / … beside it.
// self-stretch so the cell's background covers its whole row (the grid aligns
// items to the start, which would leave the empty header cell zero-height and
// let the scrolled trace names show through it).
function StickyLabelCell(props: { children?: React.ReactNode }) {
  return (
    <div className="bg-background sticky left-0 z-10 flex flex-col self-stretch leading-tight">
      {props.children}
    </div>
  );
}

// Row header (CURRENT / LAST VALUE / 7D AVG / 30D AVG) with the timestamp its
// values come from underneath, where there is one.
function StatLabel(props: { label: string; sublabel?: string }) {
  return (
    <StickyLabelCell>
      <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
        {props.label}
      </span>
      {props.sublabel !== undefined && (
        <span className="text-muted-foreground text-[9px]">
          {props.sublabel}
        </span>
      )}
    </StickyLabelCell>
  );
}

// Which lines this chart draws. Selection lives in ChartSection so the legend
// columns, the chart series and the count on this button can't disagree.
function TraceFilter(props: {
  traces: StatTrace[];
  isActive: (t: StatTrace) => boolean;
  onToggle: (t: StatTrace) => void;
  activeCount: number;
}) {
  const { t } = useLingui();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={buttonVariants({ variant: "outline", size: "sm" })}
        title={t`Choose which lines to show`}
      >
        <Trans>
          {props.activeCount} of {props.traces.length}
        </Trans>
        <ChevronDown size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-w-72">
        {props.traces.map((t, i) => {
          const active = props.isActive(t);
          return (
            <DropdownMenuCheckboxItem
              key={`${i}-${traceKey(t)}`}
              checked={active}
              // The last line can't be turned off: an empty chart with an empty
              // legend reads as broken, with nothing on screen explaining why.
              disabled={active && props.activeCount <= 1}
              // Keep the menu open — picking lines is usually several clicks.
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => props.onToggle(t)}
            >
              <span
                className="flex items-start gap-1.5 text-xs"
                style={{ color: t.color }}
              >
                <Glyph shape={t.shape ?? "circle"} color={t.color} />
                {t.label}
              </span>
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Column header — the trace's name in its line colour. Hidden traces have no
// column at all, so there is no inactive state to render; TraceFilter owns
// which traces get here.
function TraceHeader(props: {
  color: string;
  label: string;
  shape: TraceShape;
}) {
  return (
    // min-w-0: without it the grid item can't shrink under the label's
    // min-content width and the longest source name overflows the panel.
    // Inline (not flex) glyph so a wrapped label's later lines use the full
    // column, and wrap-break-word so multi-word names break at their spaces.
    <div
      className="text-2xs min-w-0 pl-2 leading-tight font-medium wrap-break-word"
      style={{ color: props.color }}
    >
      <Glyph shape={props.shape} color={props.color} />
      {props.label}
    </div>
  );
}

function Glyph(props: { shape: TraceShape; color: string }) {
  const fill = props.color;
  const sw = 1.5;
  const svg = (path: React.ReactNode) => (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      className="mr-1 inline-block align-[-1px]"
      aria-hidden="true"
    >
      {path}
    </svg>
  );
  if (props.shape === "square") {
    return svg(
      <rect
        x={sw / 2}
        y={sw / 2}
        width={10 - sw}
        height={10 - sw}
        fill={fill}
        stroke={props.color}
        strokeWidth={sw}
      />,
    );
  }
  if (props.shape === "triangle") {
    return svg(
      <polygon
        points="5,1 9.5,9 0.5,9"
        fill={fill}
        stroke={props.color}
        strokeWidth={sw}
        strokeLinejoin="round"
      />,
    );
  }
  if (props.shape === "arrow") {
    // Right-pointing arrowhead, echoing the chart's end-of-line arrow.
    return svg(
      <polygon
        points="1,1.5 9,5 1,8.5"
        fill={fill}
        stroke={props.color}
        strokeWidth={sw}
        strokeLinejoin="round"
      />,
    );
  }
  return svg(
    <circle
      cx="5"
      cy="5"
      r={5 - sw / 2}
      fill={fill}
      stroke={props.color}
      strokeWidth={sw}
    />,
  );
}

function ValueCell(props: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-1 pl-2 leading-tight">
      {props.children}
    </div>
  );
}

function PctBadge(props: { pct: number | null }) {
  if (props.pct === null) return null;
  const abs = Math.abs(props.pct);
  if (abs < 0.5) {
    return (
      <span className="text-muted-foreground text-[10px] tabular-nums">·</span>
    );
  }
  const isUp = props.pct > 0;
  return (
    <span
      className={`text-[10px] font-bold tabular-nums ${isUp ? "text-danger-foreground" : "text-success-foreground"}`}
    >
      {isUp ? "↑" : "↓"}
      {abs.toFixed(1)}%
    </span>
  );
}
